/**
 * GHLCustomMenuLinkModal — generates a templated Voxinity URL the agency
 * owner pastes into GoHighLevel's Settings → Custom Menu Links. When a GHL
 * user clicks that menu from inside a sub-account, GHL substitutes
 * `{{location.id}}`, `{{user.name}}`, `{{user.email}}` and the user is
 * deep-linked to the matching Voxinity sub-account.
 *
 * The link itself is generated client-side (no API roundtrip needed) — it's
 * a pure string template that depends only on:
 *   • the current agency's workspace_id (the `owner` of the deep-link)
 *   • the public Voxinity host (from REACT_APP_BACKEND_URL)
 *
 * GHL substitutes `{{location.id}}` at click time, so the link in the
 * modal is intentionally left with the literal `{{location.id}}` braces.
 */
import React, { useMemo, useState } from 'react';
import { ExternalLink, Copy, Check, Info } from 'lucide-react';

const buildCustomMenuLink = (host, ownerWorkspaceId) => {
  // GHL substitutes the {{...}} placeholders at click time. We don't URL-
  // encode them — GHL handles encoding of the substituted values itself.
  const params = [
    `owner=${encodeURIComponent(ownerWorkspaceId)}`,
    'locationid={{location.id}}',
    'allow=no',
    'u={{user.name}}',
    'c={{user.email}}',
  ].join('&');
  return `${host}/ghl/launch?${params}`;
};

const GHLCustomMenuLinkModal = ({ open, onClose, workspaceId }) => {
  const [copied, setCopied] = useState(false);

  // The "default" domain is the public host of this Voxinity install. We
  // derive it from REACT_APP_BACKEND_URL (already correctly set for both
  // preview + production envs). For v1 we expose just this one option;
  // multi-domain (white-label) selection is intentionally deferred until
  // there's an actual second domain to select.
  const defaultHost = useMemo(() => {
    const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
    if (raw) return raw;
    // Fallback to the current browser origin if the env is missing for
    // some reason — keeps the modal usable rather than blank.
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
  }, []);

  const customMenuLink = useMemo(
    () => (workspaceId ? buildCustomMenuLink(defaultHost, workspaceId) : ''),
    [defaultHost, workspaceId],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(customMenuLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard may be blocked (eg. insecure context) — fall back to a
      // textarea select. Silent failure isn't great here so we expose
      // a tiny inline error.
      setCopied(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ghl-custom-menu-link-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-space-900 border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Custom Menu Link</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none px-2"
            aria-label="Close"
            data-testid="ghl-custom-menu-link-close-btn"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Info panel */}
          <div className="flex gap-3 rounded-xl bg-teal-400/[0.08] border border-teal-400/25 px-4 py-3">
            <Info size={18} className="text-teal-600 dark:text-teal-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-teal-900 dark:text-teal-100/90">
              Paste this link into your GoHighLevel <strong>Settings → Custom Menu Links</strong> section
              (without editing it). Show it on <strong>sub-accounts only</strong> — not the agency level,
              since the agency itself isn&apos;t a sub-account. When a GHL user clicks the menu from inside
              a sub-account, they&apos;ll land on the matching Voxinity sub-account dashboard.
            </p>
          </div>

          {/* Domain selector — single-option dropdown for v1. Surfacing
              it now so the affordance exists; multi-domain (custom
              white-label hosts) will plug into the same UI later. */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 uppercase tracking-wide mb-1.5">
              Select domain
            </label>
            <div className="relative">
              <select
                className="w-full appearance-none px-4 py-2.5 rounded-xl bg-space-800 border border-white/10 text-white text-sm focus:outline-none focus:border-teal-400/50 cursor-not-allowed opacity-90"
                disabled
                data-testid="ghl-custom-menu-link-domain-select"
              >
                <option>Default ({(defaultHost || '').replace(/^https?:\/\//, '')})</option>
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">▼</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              Custom white-label domain support is coming soon. Right now all installs use the default Voxinity host.
            </p>
          </div>

          {/* The link card */}
          <div className="rounded-xl bg-gray-50 dark:bg-space-800/70 border border-gray-200 dark:border-white/10 p-4 flex gap-3 items-start">
            <div className="w-9 h-9 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center flex-shrink-0">
              <ExternalLink size={16} className="text-teal-600 dark:text-teal-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white mb-1">Custom menu link</p>
              <p
                className="text-[11.5px] text-gray-300 break-all font-mono leading-relaxed select-all"
                data-testid="ghl-custom-menu-link-value"
              >
                {customMenuLink}
              </p>
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0 ${
                copied
                  ? 'bg-teal-400 text-space-900'
                  : 'bg-white/10 hover:bg-white/15 text-white border border-white/10'
              }`}
              data-testid="ghl-custom-menu-link-copy-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {/* Step-by-step guide — collapsible. We'll add a Wistia video
              embed here later once one is recorded. */}
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-300 hover:text-white font-medium select-none flex items-center gap-2">
              <span className="text-teal-600 dark:text-teal-300">▸</span> How to use this link
            </summary>
            <ol className="mt-3 ml-1 space-y-2.5 text-[13px] text-gray-300 leading-relaxed list-none counter-reset-step">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-700 dark:text-teal-200 text-[11px] font-bold flex items-center justify-center mt-0.5">1</span>
                <span>Click <strong className="text-white">Copy</strong> above to copy the templated link.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-700 dark:text-teal-200 text-[11px] font-bold flex items-center justify-center mt-0.5">2</span>
                <span>In GoHighLevel, open <strong className="text-white">Settings → Custom Menu Links</strong> at the <strong className="text-white">agency level</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-700 dark:text-teal-200 text-[11px] font-bold flex items-center justify-center mt-0.5">3</span>
                <span>Click <strong className="text-white">+ Add Custom Menu Link</strong>, give it a name like <em className="text-teal-700 dark:text-teal-200 not-italic">&ldquo;Voxinity AI&rdquo;</em>, and paste the copied URL into the URL field — <strong className="text-white">do not edit it</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-700 dark:text-teal-200 text-[11px] font-bold flex items-center justify-center mt-0.5">4</span>
                <span>Set <strong className="text-white">Show on sub-accounts</strong> to <strong className="text-teal-700 dark:text-teal-200">ON</strong> and <strong className="text-white">Show on agency</strong> to <strong className="text-rose-600 dark:text-rose-300">OFF</strong> (the agency itself isn&apos;t a sub-account, so it won&apos;t resolve).</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-700 dark:text-teal-200 text-[11px] font-bold flex items-center justify-center mt-0.5">5</span>
                <span>Save. From now on, any user inside a GHL sub-account connected to Voxinity will see a <em className="text-teal-700 dark:text-teal-200 not-italic">Voxinity AI</em> menu item that deep-links straight to the matching sub-account dashboard here.</span>
              </li>
            </ol>
          </details>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-space-900/80">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-teal-400 text-space-900 hover:bg-teal-500"
            data-testid="ghl-custom-menu-link-done-btn"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default GHLCustomMenuLinkModal;
